<template>
  <div class="publico">
    <div class="cabecalho">
      <h2>{{ dados && dados.nome ? dados.nome : 'Painel compartilhado' }}</h2>
      <div class="sub">Business Intelligence — Secretaria Municipal de Educação</div>
    </div>

    <div v-if="carregando" class="estado" data-test="estado-carregando">
      <i class="el-icon-loading"></i> Abrindo o painel…
    </div>

    <!--
      NEGATIVA COMPREENSÍVEL — nunca dados parciais.
      Link expirado, revogado ou adulterado não pode renderizar meia tela: o visitante
      precisa saber o que aconteceu e a quem pedir um link novo.
    -->
    <el-alert v-else-if="erro" type="error" :closable="false" show-icon
              data-test="estado-erro" :title="tituloErro">
      <div class="obs">{{ erro }}</div>
      <div class="obs u-mt-5">
        Solicite um novo link a quem compartilhou este painel com você.
      </div>
    </el-alert>

    <template v-else-if="dados">
      <div class="meta" data-test="meta">
        <span><b>Data de referência:</b> {{ dados.data_referencia || '—' }}</span>
        <span v-if="temFiltros"><b>Filtros:</b> {{ resumoFiltros }}</span>
      </div>

      <el-alert v-if="!dados.series || !dados.series.length" type="info" :closable="false"
                show-icon data-test="estado-vazio"
                title="Sem dados para os critérios deste link"
                :description="dados.aviso || 'O painel abriu normalmente, mas não há registros para o recorte compartilhado.'"></el-alert>

      <template v-else>
        <div class="total" data-test="total">
          <div class="total-rotulo">Total</div>
          <div class="total-valor">{{ dados.total }}</div>
        </div>

        <table class="table table-striped" data-test="tabela">
          <thead><tr><th>Categoria</th><th style="width:140px">Valor</th></tr></thead>
          <tbody>
            <tr v-for="(l, i) in dados.series" :key="i">
              <td>{{ l.rotulo }}</td>
              <td>{{ l.valor }}</td>
            </tr>
          </tbody>
        </table>
      </template>

      <div class="rodape">
        Este link é somente leitura e tem validade limitada. O recorte institucional exibido é
        o mesmo de quem o gerou.
      </div>
    </template>
  </div>
</template>

<script>
/**
 * FASE 5 / BLOCO BA-4 — BI 11 e 17: painel aberto por link assinado.
 *
 * Tela PÚBLICA — sem login. Existe porque o link gerado em `bi/acesso` aponta para cá; sem
 * ela, o compartilhamento seria um endpoint órfão e o passo P29 da POC não seria executável
 * pela interface.
 *
 * O que esta tela NÃO faz, deliberadamente:
 *
 * - não lê `instituicao_id` nem qualquer filtro da URL. O escopo vem do REGISTRO do link,
 *   congelado no servidor na criação. Trocar a query string não muda nada;
 * - não renderiza dados parciais quando o link falha. Expirado, revogado ou adulterado
 *   produzem uma negativa explícita — meia tela seria pior que nenhuma.
 */
export default {
  data () {
    return { carregando: true, erro: null, motivo: null, dados: null }
  },

  computed: {
    temFiltros () {
      const f = this.dados && this.dados.filtros ? this.dados.filtros : {}
      return Object.keys(f).length > 0
    },
    resumoFiltros () {
      const f = this.dados.filtros || {}
      return Object.keys(f).map(function (k) { return k + '=' + f[k] }).join(', ')
    },
    tituloErro () {
      const t = {
        expirado: 'Este link expirou',
        revogado: 'Este link foi revogado',
        inexistente: 'Link inválido'
      }
      return t[this.motivo] || 'Não foi possível abrir o painel'
    }
  },

  mounted () { this.abrir() },

  methods: {
    base () { return this.$store.state.config.baseUrlApi },

    abrir () {
      const self = this
      const token = this.$route.params.token

      if (!token) {
        this.carregando = false
        this.motivo = 'inexistente'
        this.erro = 'Nenhum token informado no endereço.'
        return
      }

      // Sem cabeçalho de autenticação: é rota pública por desenho.
      this.$axios.get(this.base() + '/api/publico/bi/painel/' + encodeURIComponent(token))
        .then(function (r) {
          self.carregando = false
          const d = r.data || {}
          if (d.erro) {
            self.motivo = d.motivo || null
            self.erro = typeof d.erro === 'string' ? d.erro : 'Link inválido.'
            return
          }
          self.dados = d
        })
        .catch(function (e) {
          self.carregando = false
          const r = e && e.response ? e.response : null
          if (r && r.data && r.data.erro) {
            self.motivo = r.data.motivo || null
            self.erro = typeof r.data.erro === 'string' ? r.data.erro : 'Link inválido.'
            return
          }
          if (r && r.status === 429) {
            self.erro = 'Muitas tentativas. Aguarde um instante e recarregue.'
            return
          }
          self.erro = 'Não foi possível contatar o servidor.'
        })
    }
  }
}
</script>

<style scoped>
.publico { max-width: 900px; margin: 24px auto; padding: 0 16px; }
.cabecalho { border-bottom: 1px solid #ebeef5; padding-bottom: 10px; margin-bottom: 16px; }
.cabecalho h2 { margin: 0; font-size: 20px; }
.sub { font-size: 12px; color: #909399; }
.estado { padding: 40px; text-align: center; color: #909399; }
.meta { font-size: 12px; color: #606266; margin-bottom: 12px; }
.meta span { margin-right: 18px; }
.total { background: #f5f7fa; border-radius: 4px; padding: 14px; display: inline-block; margin-bottom: 12px; }
.total-rotulo { font-size: 12px; color: #909399; }
.total-valor { font-size: 30px; font-weight: 600; }
.rodape { font-size: 11px; color: #909399; margin-top: 18px; border-top: 1px solid #ebeef5; padding-top: 8px; }
.obs { font-size: 12px; }
</style>
